/**
 * Who gets past the pre-launch wall.
 *
 * Split out of PasswordGate.js so the rules can be tested without importing
 * React or react-router. These decide whether real organizers can reach their
 * own dashboard before launch, which is not something to discover from a
 * support message.
 */
import { CITY_SLUGS } from '../constants/cities';

/**
 * Paths that stay reachable while the rest of the site is gated.
 *
 * The legal and support pages have to be public even pre-launch: the
 * pre-launch landing page links to the privacy policy and terms, and every
 * page a visitor can reach renders the footer, whose Company column links to
 * the whole set. A password wall on any of them makes the site read as
 * unavailable — which is also what Apple's organization enrolment checks for
 * when it verifies a company website.
 *
 * The product itself (/events, /magazine, /submit-event) stays gated. This is
 * only the compliance surface.
 */
const PUBLIC_PATHS = new Set([
  '/privacy',
  '/terms',
  '/cookies',
  '/acceptable-use',
  '/accessibility',
  '/contact',
  '/help',
  // Organizer onboarding needs to reach token validation + setup before login.
  '/organizer-onboard',

  // The sign-in form itself. Every recovery path below finishes here —
  // ResetPasswordPage navigates to /login three seconds after a successful
  // reset — so gating it made those paths dead-end at the wall they were made
  // public to avoid. It also stranded early-access organizers: the invite
  // token is single-use, so once their session ended (7-day cookie, or simply
  // opening Vibana on a second device) there was no way back in at all.
  // A sign-in form is not product surface, and it is useless without an
  // account, which is the same reasoning that put the paths above here.
  '/login',

  // Sign-up, which is the same form. /register and /login both render
  // AuthPage; only the tab differs. Leaving one open and the other gated was
  // not a decision, it was the half of a pair nobody looked at.
  //
  // It is also the single link in the launch broadcast. buildLaunchEmail sends
  // the WHOLE waitlist to /register?email=&name=, so with this gated the email
  // that announces Vibana is live lands every one of those readers on a
  // password form — the one send where a dead end cannot be repaired, because
  // there is no second launch day.
  //
  // Opening it costs nothing pre-launch: signup runs through POST /otp/send,
  // which mails a six-digit code to the address given and creates no account
  // until that code comes back. The gate is a password on a marketing site,
  // not an authorization check, and the endpoints behind this page do their
  // own regardless of what this list says.
  '/register',

  // Links the backend has already emailed to real people. services/email.js
  // builds these from APP_URL, which is vibana.app — the domain this gate now
  // answers on. Behind the password wall a locked-out user cannot recover their
  // own account: the reset link lands on a form asking for the very password
  // they do not have. These are account recovery, not product surface, so they
  // stay reachable while everything else waits for launch.
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/verify-email-change',

  // Pages a reader can only reach from a link we emailed them, each carrying
  // its own single-use token. They are the same shape as /organizer-onboard
  // above: the token is the credential, the page is useless without it, and
  // the endpoint behind it checks the token whatever this list says.
  //
  // Gating them breaks the one journey the email exists to start. A guest
  // buyer's ticket email offers a refund link; a partner gets a proposal link;
  // a buyer returns from Stripe to the confirmation. All three landed on a
  // password form instead, which reads as the link being broken.
  '/refund-request',
  '/payment-success',

  // The ticket itself. Every ticket email links here with the ticket's own
  // code, and a guest has no account to sign into — so a password wall on
  // this page is a buyer standing at a door unable to produce their ticket.
  // The endpoint behind it takes the code as its credential and returns an
  // allowlist of admission details, no money and no buyer identity.
  '/ticket',

  // The organizer application form, and the redirect that points at it.
  //
  // The landing page's early-access form refuses an email that already has an
  // account and tells the applicant to sign in and apply from inside the app.
  // /login was public and this was not, so following that instruction landed on
  // the password wall — the one route the pre-launch intake explicitly sends
  // people down was the one route that dead-ended.
  //
  // The page is safe to open: logged out it renders "Sign in to create your
  // organizer profile" rather than the form, and the endpoint behind the form
  // requires auth regardless of what this list says.
  '/work-with-us/organizers',
  '/become-organizer',

  // How an organizer gets paid. Public on purpose, and not only because the
  // application form above is: somebody deciding whether to commit an event
  // will show this to a venue, a partner or an accountant, and a page that
  // asks the second reader for a password fails at the moment it matters.
  // The figures are in the Terms already, which is public too.
  '/organizers/how-you-get-paid',
]);

/**
 * The consumer browse surfaces, which an early-access organizer does not get.
 *
 * Their bypass is not path-aware — one flag and they saw the whole app. But
 * pre-launch they are here to build events, and the feed, the city pages and
 * the magazine are the half that is not ready for anyone yet. Letting them
 * roam it shows them an empty product and invites feedback on something that
 * is not finished.
 *
 * /tv is here because it redirects to /magazine?tab=videos, so leaving it open
 * would land an organizer on the page above it.
 */
const ORGANIZER_BLOCKED = [
  /^\/$/,
  /^\/events$/,
  /^\/magazine(\/|$)/,
  /^\/tv$/,
];

/**
 * /events/:id is two different pages wearing the same URL.
 *
 * /events/houston is a city browse page; /events/afrobeats-jollof-ab12 and
 * /events/68f2… are one event. App.js's EventRouteResolver tells them apart by
 * looking the segment up in CITY_SLUGS, and this does the same — it has to, or
 * the two would disagree about what a path is.
 *
 * Both of these used to be open, on the reasoning that they could not be told
 * apart before render. They can: same lookup, same list, now imported from one
 * place so a new city cannot be added to the router and missed here.
 *
 * The event page stays open because it is how an organizer previews their own
 * work — OrganizerEventsPanel's "View" links to /events/<slug or id>. This
 * check cannot tell whose event it is, only that it is an event and not a city
 * page; EventDetailPage does the ownership half, since it is the thing that
 * actually knows who the organizer is.
 */
function isCityBrowsePath(normalised) {
  const match = normalised.match(/^\/events\/([^/]+)$/);
  return Boolean(match && CITY_SLUGS.has(match[1]));
}

function isConsumerBrowse(pathname) {
  const normalised = (pathname || '').toLowerCase().replace(/\/+$/, '') || '/';
  return ORGANIZER_BLOCKED.some((re) => re.test(normalised)) || isCityBrowsePath(normalised);
}

/**
 * Public paths that carry a parameter, so the exact-match Set cannot hold them.
 *
 * /proposal/:token is a partnership proposal we emailed to one recipient. The
 * token is the credential and the page is meaningless without it.
 */
const PUBLIC_PREFIXES = ['/proposal/'];

// Trailing slashes and casing vary by however the visitor arrived at the link.
function isPublicPath(pathname) {
  const normalised = (pathname || '').toLowerCase().replace(/\/+$/, '') || '/';
  if (PUBLIC_PATHS.has(normalised)) return true;
  return PUBLIC_PREFIXES.some((p) => normalised.startsWith(p) && normalised.length > p.length);
}

/**
 * Who gets past the wall, as a plain function of role and path.
 *
 * Exported so it can be tested. The rules below decide whether real organizers
 * can reach their own dashboard before launch, and that is not something to
 * discover from a support message.
 *
 * @param {string|undefined} role     the signed-in user's role, if any
 * @param {string} pathname           the path being visited
 * @param {boolean} siteLocked        SITE_LOCKED
 * @param {string} sitePassword       SITE_PASSWORD ('' disables the gate)
 * @returns {'open'|'bypass'|'gate'}  open = gate switched off entirely
 */
export function gateDecision(role, pathname, siteLocked, sitePassword) {
  if (!siteLocked || !sitePassword) return 'open';

  // The bypass keys off the ROLE, not off organizerEarlyAccessUnlocked.
  //
  // That flag is written in exactly one place — accept-organizer-invite, the
  // handler behind the emailed /organizer-onboard?token= link. Every other way
  // an organizer comes into existence leaves it false: the admin panel's
  // "create user" and "change role" both write `role` and nothing else, and so
  // did POST /api/auth/register until it stopped accepting the field. An
  // organizer made any of those ways reached their own dashboard and got the
  // password wall, with no link to click and nothing on screen explaining what
  // they were missing.
  //
  // Keying off the role is not a weaker check. `organizer` is only ever granted
  // by an admin — by approving an application, or by setting it directly — so
  // the role already carries the approval the flag was standing in for.
  //
  // It also drops a localStorage flag that used to sit in the same condition.
  // Both of the original clauses ANDed it with role === 'organizer', so a bare
  // role check already covers everything it allowed; reading it on its own
  // would have let any visitor past with one line in devtools.
  //
  // admin and support are here for the same reason organizers are: staff were
  // walled out of their own dashboards. /admin is a separate app, but
  // SupportDashboard lives in this one behind /support/*. Staff are not held
  // out of the consumer surfaces either — an admin checking how the feed
  // renders is the whole point of the account.
  if (role === 'admin' || role === 'support') return 'bypass';
  if (isPublicPath(pathname)) return 'bypass';
  if (role === 'organizer' && !isConsumerBrowse(pathname)) return 'bypass';

  return 'gate';
}

/**
 * May this viewer see this event's page?
 *
 * The other half of the /events/:id rule. gateDecision lets an organizer
 * through to any event page because a path cannot say whose event it is;
 * EventDetailPage calls this once the event has loaded and it can.
 *
 * Fails OPEN when the organizer id is missing. The event endpoint normally
 * populates it, but if it ever does not, the two outcomes are not symmetric:
 * failing open shows one organizer another's event, while failing closed hides
 * an organizer's own event from them and makes the dashboard's "View" button
 * look broken. This is a tidiness rule about an unopened site, not a
 * confidentiality boundary — the API serves the same event to anyone who asks.
 *
 * @param {string|undefined} role        the viewer's role
 * @param {boolean} siteLocked           SITE_LOCKED
 * @param {string|undefined} viewerId    the viewer's user id
 * @param {string|undefined} organizerId the event's organizer id
 */
export function canOrganizerViewEvent(role, siteLocked, viewerId, organizerId) {
  if (!siteLocked) return true;
  if (role !== 'organizer') return true;
  if (!organizerId || !viewerId) return true;
  return String(organizerId) === String(viewerId);
}
